import Modal from '@hero/ui-kit/components/Modal';
import Button from '@hero/ui-kit/inputs/Button';
import Form from '@hero/ui-kit/inputs/Form';
import Input from '@hero/ui-kit/inputs/Input';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import React from 'react';
import changeEmailSchema, { ChangeEmailParams } from './schema';
import useUpdateUserEmail from '../../api/useUpdateUserEmail';
import { useSelector } from 'react-redux';
import { isAppSupportAdminAdminSelector } from '@hero/redux-data/backoffice/userStatus/selectors';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getUserDeviceDetails } from '@hero/redux-data/backoffice/userDeviceDetails/actionCreators';
import QueryInlineError from '../../../../components/QueryInlineError';
import useRevokeUserTokens from '../../api/useRevokeUserTokens';
import StandaloneCheckbox from '@hero/ui-kit/inputs/StandaloneCheckbox';

interface EmailChangeProps {
    id?: number;
    email?: string;
    firstName?: string;
    lastName?: string;
}

const EmailChange: React.FC<EmailChangeProps> = ({ email, firstName, lastName, id }) => {
    const dispatch = useDispatch();

    const name = React.useMemo(() => {
        if (firstName || lastName) {
            return `${firstName} ${lastName}`;
        }

        return '-';
    }, [firstName, lastName]);

    const [showEdit, setShowEdit] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState(
        `Successfully changed the email for ${name}`
    );
    const isAppSupportAdmin = useSelector(isAppSupportAdminAdminSelector);
    const { user_id } = useParams();

    const {
        mutateAsync,
        isError,
        isPending,
        isSuccess,
        error,
        reset: resetUpdateEmail
    } = useUpdateUserEmail(id || 0);
    const {
        mutate: revokeTokens,
        error: RevokeTokensError,
        isError: isRevokeTokensError,
        reset: resetRevokeTokens
    } = useRevokeUserTokens();

    const handleClose = () => {
        resetUpdateEmail();
        resetRevokeTokens();
        setShowEdit(false);
        setSuccessMessage(`Successfully changed the email for ${name}`);
        user_id && dispatch(getUserDeviceDetails({ user_id: +user_id }));
    };

    const handleSubmit = (attributes: ChangeEmailParams) => {
        if (id) {
            if (attributes.revoke_tokens) {
                setSuccessMessage(
                    `Successfully changed the email and revoked all tokens for ${name}`
                );
            }

            mutateAsync(attributes)
                .then(() => {
                    if (attributes.revoke_tokens) {
                        revokeTokens({ hero_user_id: id });
                    }
                })
                .catch((error) => {
                    console.error('Error updating email:', error);
                });
        }
    };

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: '1rem',
                    height: '2rem'
                }}
            >
                <P noDefaultMargin>{email || '-'}</P>
                <div title={!isAppSupportAdmin ? 'Only app support admin can change email' : ''}>
                    <Button
                        size="small"
                        onClick={() => setShowEdit(true)}
                        disabled={!isAppSupportAdmin}
                    >
                        Edit
                    </Button>
                </div>
            </div>
            <Modal
                type={isSuccess ? 'success' : 'neutral'}
                onClose={handleClose}
                isCancelable
                externalControls={[showEdit, setShowEdit]}
                styles={{ paddingTop: '3.4rem' }}
            >
                {isSuccess ? (
                    <>
                        <P centered size="large" strong>
                            {successMessage}
                        </P>
                        <Button onClick={handleClose} width="full">
                            Close
                        </Button>
                    </>
                ) : (
                    <>
                        <H centered role="h5">{`Change the email for ${name}`}</H>
                        <P>{`Previous email: ${email}`}</P>
                        <div style={{ minWidth: '40rem', padding: '0 2rem' }}>
                            <Form validationSchema={changeEmailSchema} submitFn={handleSubmit}>
                                <Section noDefaultPadding>
                                    <Input
                                        name="email"
                                        displayName="New Email Address"
                                        placeholder="Enter new email"
                                        styles={{ width: '40rem' }}
                                    />
                                </Section>
                                <StandaloneCheckbox
                                    name="revoke_tokens"
                                    labelText="Logout user from all devices (Revoke tokens)"
                                    styles={{ marginTop: '1.4rem' }}
                                />
                                <div style={{ marginTop: '4.2rem' }}>
                                    <Button type="submit" width="full" disabled={isPending}>
                                        Update
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={handleClose}
                                        width="full"
                                        disabled={isPending}
                                    >
                                        Cancel
                                    </Button>
                                </div>

                                {isError || isRevokeTokensError ? (
                                    <QueryInlineError centered error={error || RevokeTokensError} />
                                ) : null}
                            </Form>
                        </div>
                    </>
                )}
            </Modal>
        </>
    );
};

export default EmailChange;
